import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Alert,
  Box,
  SkeletonLoader,
  Space,
  TextBody,
  TextHeading,
  get,
} from '@ally/metronome-ui'
import styled from 'styled-components'
import allytm from '@ally/allytm'
import { NonIraAccountView } from './NonIraAccountView'
import { AccountDetails } from '../../types'
import { useNonIraBeneficiaries } from '../../services/NonIraBeneficiariesProvider'
import { LearnMoreFdicModal } from './LearnMoreFdicModal'
import { smoothScrollTo } from '../../utils/smoothScrollto'
import { NonPrintingWrapper } from '../../utils/NonPrintingWrapper'

const StyledAccountList = styled.ul`
  list-style-type: none;
  padding: 0;
  li {
    margin-bottom: ${get.space('xs')};
    line-height: ${get.space('md')};
    padding-top: ${get.space('xl')};
  }
  li:first-child {
    padding-top: ${get.space('lg')};
  }
  @media print {
    li {
      page-break-inside: avoid;
    }
  }
`

export const NonIraBeneficiaries: React.FC<{ headerRef: any }> = ({
  headerRef,
}) => {
  const { accounts, accountsLoading, hasAccountsError } =
    useNonIraBeneficiaries()
  const { state: locationState } = useLocation<{ deepLinkId: string }>()

  const activeAccounts = accounts?.filter(
    account => account.accountStatus.toLowerCase() === 'active',
  )

  useEffect(() => {
    if (!accountsLoading && locationState && locationState.deepLinkId) {
      // istanbul ignore next - page scrolling, focus
      requestAnimationFrame(() => {
        smoothScrollTo({
          duration: 2000,
          id: `${locationState.deepLinkId.trim()}-heading`,
        })
        // istanbul ignore next - location state would not contain account id if it didn't exist
        document
          .getElementById(`${locationState.deepLinkId.trim()}-heading`)
          ?.focus()
        locationState.deepLinkId = ''
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsLoading])

  useEffect(() => {
    allytm.event('pageview', 'AOS:Beneficiaries:Bank Checking Savings and CDs')
  }, [])

  return (
    <Box data-testid="deposit-content">
      <Space pt="md">
        <TextHeading
          tag="h2"
          size="md"
          weight="bold"
          tabIndex={-1}
          ref={headerRef}
        >
          Checking, Savings and CDs
        </TextHeading>
        <TextBody tag="p" size="sm">
          You can choose up to 10 beneficiaries for each of your non-IRA
          accounts. If you have more than one beneficiary, each will receive an
          equal amount of money from your account unless you adjust your
          allocations.
        </TextBody>
        <TextBody tag="p" size="sm">
          You may qualify for additional FDIC coverage when you add
          beneficiaries.{' '}
          <NonPrintingWrapper>
            <LearnMoreFdicModal />
          </NonPrintingWrapper>
        </TextBody>
      </Space>
      <>
        {accountsLoading ? (
          <Box pt="xl" data-testid="non-ira-loader">
            <SkeletonLoader.Container>
              <SkeletonLoader.Row width="xl" height="l" />
              <SkeletonLoader.Row width="xl" height="l" />
            </SkeletonLoader.Container>
          </Box>
        ) : (
          <>
            {hasAccountsError ? (
              <Box mt="md">
                <Alert
                  disableContainer
                  variant="error"
                  body="It's us - we're having a moment. We couldn't load your beneficiaries right now. We'll be quick about it, so check back in a bit."
                  focusOnMount
                />
              </Box>
            ) : (
              <StyledAccountList>
                {
                  /* istanbul ignore next */ activeAccounts?.map(
                    (account: AccountDetails) => (
                      <li key={account.accountId}>
                        <NonIraAccountView account={account} />
                      </li>
                    ),
                  )
                }
              </StyledAccountList>
            )}
          </>
        )}
      </>
    </Box>
  )
}
